import { apiClient } from "./client";

/**
 * Functional (business) roles — Med 1, ARM 2, coordonnateur.
 * Distinct from RBAC roles: this is what a practitioner *is* during a shift,
 * with the workstation (phone line, desk) that goes with it.
 */
export type RolePost = {
  id: string;
  functional_role_id: string;
  organization_id: string;
  phone_line: string | null;
  sip_extension: string | null;
  desk_label: string | null;
  radio_channel: string | null;
  instructions: string | null;
  is_active: boolean;
};

export type FunctionalRole = {
  id: string;
  tenant_id: string;
  code: string;
  label: string;
  description: string | null;
  color: string | null;
  sort_order: number;
  is_active: boolean;
  posts: RolePost[];
};

export async function listFunctionalRoles(opts?: {
  isActive?: boolean;
  organizationId?: string;
}): Promise<FunctionalRole[]> {
  const params: Record<string, unknown> = {};
  if (opts?.isActive !== undefined) params.is_active = opts.isActive;
  if (opts?.organizationId) params.organization_id = opts.organizationId;
  const { data } = await apiClient.get<FunctionalRole[]>("/api/v1/functional-roles", {
    params,
  });
  return data;
}

export type FunctionalRolePayload = {
  code: string;
  label: string;
  description?: string | null;
  color?: string | null;
  sort_order?: number;
  is_active?: boolean;
};

export async function createFunctionalRole(
  p: FunctionalRolePayload,
): Promise<FunctionalRole> {
  const { data } = await apiClient.post<FunctionalRole>("/api/v1/functional-roles", p);
  return data;
}

export async function patchFunctionalRole(
  id: string,
  p: Partial<FunctionalRolePayload>,
): Promise<FunctionalRole> {
  const { data } = await apiClient.patch<FunctionalRole>(
    `/api/v1/functional-roles/${id}`,
    p,
  );
  return data;
}

export async function deleteFunctionalRole(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/functional-roles/${id}`);
}

export type RolePostPayload = {
  organization_id: string;
  phone_line?: string | null;
  sip_extension?: string | null;
  desk_label?: string | null;
  radio_channel?: string | null;
  instructions?: string | null;
  is_active?: boolean;
};

export async function createRolePost(
  roleId: string,
  p: RolePostPayload,
): Promise<RolePost> {
  const { data } = await apiClient.post<RolePost>(
    `/api/v1/functional-roles/${roleId}/posts`,
    p,
  );
  return data;
}

export async function patchRolePost(
  roleId: string,
  postId: string,
  p: Partial<Omit<RolePostPayload, "organization_id">>,
): Promise<RolePost> {
  const { data } = await apiClient.patch<RolePost>(
    `/api/v1/functional-roles/${roleId}/posts/${postId}`,
    p,
  );
  return data;
}

export async function deleteRolePost(roleId: string, postId: string): Promise<void> {
  await apiClient.delete(`/api/v1/functional-roles/${roleId}/posts/${postId}`);
}
